import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { JobApplicationsFilterParams } from '@/types/job-application'
import { useJobApplicationsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'
import { useQuery } from '@tanstack/react-query'
import { downloadJobApplicationPdf } from '@/lib/api'

export const useJobApplications = (params: JobApplicationsFilterParams) => {
  const fetchJobApplications = useJobApplicationsStore(state => state.fetchJobApplications)

  return useQuery({
    queryKey: ['job-applications', params],
    queryFn: () => fetchJobApplications(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useJobApplication = (id: string) => {
  const getJobApplication = useJobApplicationsStore(state => state.getJobApplication)

  return useQuery({
    queryKey: ['job-application', id],
    queryFn: () => getJobApplication(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useDeleteJobApplication = () => {
  const deleteJobApplication = useJobApplicationsStore(state => state.deleteJobApplication)

  return useMutation({
    mutationFn: (id: string) => deleteJobApplication(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteJobApplications = () => {
  const bulkDeleteJobApplications = useJobApplicationsStore(state => state.bulkDeleteJobApplications)
  const deselectAllJobApplications = useJobApplicationsStore(state => state.deselectAllJobApplications)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteJobApplications(ids),
    onSuccess: response => {
      deselectAllJobApplications()
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useDownloadJobApplicationPdf = () => {
  const t = useTranslations()

  return useMutation({
    mutationFn: (id: string) => downloadJobApplicationPdf(id),
    onSuccess: ({ blob, filename }, id) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = filename || `job-application-${id}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message ?? t('error_downloading_file'))
    }
  })
}
